import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { ArrowRight, CheckCircle2, Clock } from 'lucide-react';
import { toast } from 'sonner';

const Register = () => {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: '', email: '', password: '' });
  const [loading, setLoading] = useState(false);
  const [pending, setPending] = useState(null); // { name, email }

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    const res = await register(form);
    setLoading(false);
    if (res.success) {
      if (res.pending) {
        setPending({ name: form.name, email: form.email });
        toast.success('Cuenta creada · Pendiente de aprobación');
      } else {
        toast.success('Cuenta creada · ¡Bienvenido!');
        navigate('/app');
      }
    } else {
      toast.error(res.error || 'Error al registrar');
    }
  };

  if (pending) {
    return (
      <div className="min-h-screen flex">
        <div className="flex-1 flex items-center justify-center px-6 py-12">
          <div className="w-full max-w-sm fade-in" data-testid="register-pending-screen">
            <Link to="/" className="inline-flex items-center gap-2 mb-10">
              <div className="w-8 h-8 rounded-md pc-logo-tile flex items-center justify-center">
                <span className="text-white font-semibold text-[15px] leading-none tracking-tight">P</span>
              </div>
              <span className="text-base tracking-tight font-medium">PriceCapture · UNO</span>
            </Link>

            <div className="w-12 h-12 rounded-full bg-[#fef3c7] flex items-center justify-center mb-5">
              <Clock size={22} className="text-[#92400e]" strokeWidth={1.6} />
            </div>

            <h1 className="font-serif text-[36px] leading-[1.05] tracking-tight mb-2">
              Cuenta <em className="text-[#8a8579]">pendiente.</em>
            </h1>
            <p className="text-[14px] text-[#5a5a5a] mb-6 leading-relaxed">
              Hola <span className="font-medium text-[#1c1c1c]">{pending.name}</span>, tu cuenta fue creada correctamente.
              Un administrador debe aprobarla antes de que puedas iniciar sesión.
            </p>

            <div className="bg-[#f3f0e9] border border-[#e8e5de] rounded-lg p-4 space-y-2 text-[12.5px]">
              <div className="flex items-center gap-2 text-[#3a3a3a]">
                <CheckCircle2 size={14} className="text-[#15803d]"/>
                <span>Solicitud enviada al equipo de administración</span>
              </div>
              <div className="flex items-center gap-2 text-[#5a5a5a]">
                <Clock size={14} className="text-[#92400e]"/>
                <span>Recibirás un correo cuando sea aprobada</span>
              </div>
              <div className="pt-2 mt-2 border-t border-[#e8e5de] font-mono text-[11.5px] text-[#5a5a5a]">
                {pending.email}
              </div>
            </div>

            <Button
              onClick={() => navigate('/login')}
              className="w-full h-11 bg-[#0f1d6f] hover:bg-[#0b1a52] text-white rounded-full mt-6 group"
              data-testid="register-pending-go-login"
            >
              Volver a inicio de sesión <ArrowRight size={15} className="ml-2 group-hover:translate-x-0.5 transition-transform" />
            </Button>
          </div>
        </div>

        <div className="hidden lg:block flex-1 relative overflow-hidden">
          <img src="/assets/login-bg.jpg" alt="PriceCapture" className="absolute inset-0 w-full h-full object-cover"/>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex">
      <div className="flex-1 flex items-center justify-center px-6 py-12">
        <div className="w-full max-w-sm fade-in">
          <Link to="/" className="inline-flex items-center gap-2 mb-10">
            <div className="w-8 h-8 rounded-md pc-logo-tile flex items-center justify-center">
              <span className="text-white font-semibold text-[15px] leading-none tracking-tight">P</span>
            </div>
            <span className="text-base tracking-tight font-medium">PriceCapture · UNO</span>
          </Link>

          <h1 className="font-serif text-[40px] leading-[1.05] tracking-tight mb-2">Crear <em className="text-[#8a8579]">cuenta.</em></h1>
          <p className="text-[14px] text-[#5a5a5a] mb-2">Registra a un nuevo capturador en el sistema.</p>
          <p className="text-[12px] text-[#92400e] bg-[#fef3c7] border border-[#fde68a] rounded-md px-3 py-2 mb-7">
            Tu cuenta requerirá la aprobación de un administrador antes de poder iniciar sesión.
          </p>

          <form onSubmit={onSubmit} className="space-y-4">
            <div className="space-y-1.5">
              <Label className="text-[12px] tracking-wider uppercase text-[#8a8579]">Nombre</Label>
              <Input data-testid="register-name" required value={form.name} onChange={e => setForm({...form, name: e.target.value})} placeholder="Carlos Méndez" className="h-11 bg-transparent border-[#d4cfc4] focus:border-[#1c1c1c]" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-[12px] tracking-wider uppercase text-[#8a8579]">Correo</Label>
              <Input data-testid="register-email" type="email" required value={form.email} onChange={e => setForm({...form, email: e.target.value})} placeholder="carlos@pricecapture.gt" className="h-11 bg-transparent border-[#d4cfc4] focus:border-[#1c1c1c]" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-[12px] tracking-wider uppercase text-[#8a8579]">Contraseña</Label>
              <Input data-testid="register-password" type="password" required value={form.password} onChange={e => setForm({...form, password: e.target.value})} placeholder="Mínimo 6 caracteres" minLength={6} className="h-11 bg-transparent border-[#d4cfc4] focus:border-[#1c1c1c]" />
            </div>

            <Button data-testid="register-submit" type="submit" disabled={loading} className="w-full h-11 bg-[#0f1d6f] hover:bg-[#0b1a52] text-white rounded-full mt-3 group">
              {loading ? 'Creando…' : (<>Crear cuenta <ArrowRight size={15} className="ml-2 group-hover:translate-x-0.5 transition-transform" /></>)}
            </Button>
          </form>

          <p className="mt-7 text-[13px] text-[#5a5a5a]">
            ¿Ya tienes cuenta? <Link to="/login" className="text-[#1c1c1c] underline underline-offset-4">Iniciar sesión</Link>
          </p>
        </div>
      </div>

      <div className="hidden lg:block flex-1 relative overflow-hidden">
        <img src="/assets/login-bg.jpg" alt="PriceCapture" className="absolute inset-0 w-full h-full object-cover"/>
      </div>
    </div>
  );
};

export default Register;
